/* eslint-disable camelcase */

import React, { useState, useEffect } from 'react';
import Logo from '../img/almabetter-logo1.png';
import { Auth, Hub } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { setLoggedIn, loggedInSelector } from '../redux/reducers/userReducer';
import { setAdminDetails } from '../redux/actions/userActions';
import { getLoggedInUserRoles } from '../services/apiService';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingOverlay from '../extras/LoadingOverlay';
import { useTheme } from '../theme/useTheme';

const LoginPage = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const dispatch = useDispatch();
  const loggedIn = useSelector(loggedInSelector);
  const history = useHistory();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  const handleLogin = async () => {
    try {
      const { user: adminRoles } = await getLoggedInUserRoles();
      if (adminRoles.roles.length) {
        dispatch(setAdminDetails(adminRoles));
        setErrorMessage(null);
      }
    } catch (error) {
      const errMsg = error?.request?.response?.message || error.message;
      setErrorMessage(errMsg);
      dispatch(setLoggedIn(false));
      await Auth.signOut();
    }
  };

  async function checkIfLoggedIn() {
    try {
      setLoading(true);
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        await handleLogin();
      }
    } catch (e) {
      console.error(e);
      // show error if any in google signin
    } finally {
      setLoading(false);
    }
  }

  const listener = async ({ payload: { event, data } }) => {
    switch (event) {
      case 'signOut':
        history.replace('/dashboard');
        break;
      case 'customOAuthState':
        // Google Redirect
        history.replace(JSON.parse(data));
        break;
      case 'signIn_failure':
        setErrorMessage(data.message);
        setLoading(false);
        break;
    }
  };

  useEffect(() => {
    Hub.listen('auth', listener);
    checkIfLoggedIn();
    return () => {
      setErrorMessage(null);
      Hub.remove('auth', listener);
    };
  }, []);

  if (loggedIn) return children;

  if (loading) return <LoadingOverlay />;

  return (
    <div 
      className='h-screen flex transition-colors duration-300'
      style={{ 
        background: isDarkMode 
          ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #eff6ff 0%, #e5e7eb 100%)'
      }}
    >
      <div
        className='m-auto rounded-lg shadow-xl text-gray-600 py-16 px-10 transition-colors duration-300'
        id='login-form'
        style={{
          backgroundColor: 'var(--card-background)',
          color: 'var(--card-text)',
        }}
      >
        <img src={Logo} alt='AlmaBetter Logo' className='mx-auto px-24' />
        <button
          onClick={() =>
            Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Google,
              customState: JSON.stringify(location.pathname),
            })
          }
          className='mt-10 w-full text-base text-center py-3 border flex space-x-2 items-center justify-center rounded-lg transition-all duration-300 hover:shadow-lg'
          style={{
            borderColor: 'var(--border-main)',
            backgroundColor: 'var(--input-background)',
            color: 'var(--text-primary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-main)';
            e.currentTarget.style.backgroundColor = 'var(--primary-light)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-main)';
            e.currentTarget.style.backgroundColor = 'var(--input-background)';
          }}
        >
          <img
            src='https://www.svgrepo.com/show/355037/google.svg'
            className='w-6 h-6'
            alt=''
          />{' '}
          <span>Login with Google</span>
        </button>
        {errorMessage && (
          <div 
            className='mt-8 -mb-8 text-center rounded-lg p-3'
            style={{
              color: 'var(--error-main)',
              backgroundColor: 'var(--error-light)',
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
